/**
 * PieChartControl
 *
 * Pie/doughnut chart display
 * Single series, automatic slicing, label positioning
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import {
  BaseChartComponent,
  type ChartDataPoint,
  extractChartData,
  getSeriesConfig,
} from './BaseChartComponent';

interface PieSlice {
  name: string;
  value: number;
  color: string;
  startAngle: number;
  endAngle: number;
}

const PieChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);
  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  // Get pie chart specific config
  const pieConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      innerRadius: (config?.innerRadius as number) || 0,
      startAngle: (config?.startAngle as number) || 0,
      endAngle: (config?.endAngle as number) || 360,
      showLabels: config?.showLabels !== false,
    };
  }, [control.config]);

  // Calculate pie slices
  const slices = useMemo(() => {
    if (!chartData || chartData.length === 0 || seriesConfig.length === 0) {
      return [];
    }

    const series = seriesConfig[0];
    const values = chartData.map((d) => {
      const val = (d as Record<string, unknown>)[series.dataKey];
      return typeof val === 'number' ? val : 0;
    });

    const total = values.reduce((a, b) => a + b, 0) || 1;
    let currentAngle = pieConfig.startAngle;

    const result: PieSlice[] = chartData.map((d, index) => {
      const value = values[index] || 0;
      const sliceAngle = (value / total) * (pieConfig.endAngle - pieConfig.startAngle);
      const start = currentAngle;
      const end = currentAngle + sliceAngle;
      currentAngle = end;

      const name = String((d as Record<string, unknown>)['name'] || `Slice ${index + 1}`);
      const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c', '#d084d0'];
      const color = colors[index % colors.length];

      return {
        name,
        value,
        color,
        startAngle: start,
        endAngle: end,
      };
    });

    return result;
  }, [chartData, seriesConfig, pieConfig]);

  // Render SVG-based pie chart
  const renderPie = useMemo(() => {
    if (slices.length === 0) {
      return null;
    }

    const centerX = 300;
    const centerY = 200;
    const radius = 120;
    const innerRadius = (pieConfig.innerRadius / 100) * radius || 0;

    // Convert angle to radians and to SVG coordinates
    const getCoords = (angle: number, rad: number): [number, number] => {
      const radians = (angle - 90) * (Math.PI / 180);
      return [centerX + rad * Math.cos(radians), centerY + rad * Math.sin(radians)];
    };

    // Create SVG path for pie slice
    const createSlicePath = (slice: PieSlice): string => {
      const largeArcFlag = slice.endAngle - slice.startAngle > 180 ? 1 : 0;

      const outerStart = getCoords(slice.startAngle, radius);
      const outerEnd = getCoords(slice.endAngle, radius);

      let path = `M ${centerX} ${centerY}`;

      if (innerRadius > 0) {
        const innerStart = getCoords(slice.startAngle, innerRadius);
        path += ` L ${outerStart[0]} ${outerStart[1]}`;
        path += ` A ${radius} ${radius} 0 ${largeArcFlag} 1 ${outerEnd[0]} ${outerEnd[1]}`;
        path += ` L ${innerEnd[0]} ${innerEnd[1]}`;
        path += ` A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart[0]} ${innerStart[1]}`;
        path += ' Z';
      } else {
        path += ` L ${outerStart[0]} ${outerStart[1]}`;
        path += ` A ${radius} ${radius} 0 ${largeArcFlag} 1 ${outerEnd[0]} ${outerEnd[1]}`;
        path += ' Z';
      }

      return path;
    };

    const innerEnd = innerRadius > 0 ? getCoords(0, innerRadius) : [centerX, centerY];

    return (
      <svg viewBox="0 0 600 400" style={{ width: '100%', height: '100%' }}>
        {/* Pie slices */}
        {slices.map((slice, index) => {
          return (
            <path
              key={`slice-${index}`}
              d={createSlicePath(slice)}
              fill={slice.color}
              opacity="0.8"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}

        {/* Labels */}
        {pieConfig.showLabels &&
          slices.map((slice, index) => {
            const midAngle = (slice.startAngle + slice.endAngle) / 2;
            const labelRadius = radius * 0.65;
            const [labelX, labelY] = getCoords(midAngle, labelRadius);

            const percentage = ((slice.value / slices.reduce((a, b) => a + b.value, 0)) * 100).toFixed(1);

            return (
              <g key={`label-${index}`}>
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  fontSize="12"
                  fill="white"
                  fontWeight="bold"
                >
                  {percentage}%
                </text>
              </g>
            );
          })}

        {/* Legend */}
        {(control.config?.legend as Record<string, unknown> | undefined)?.visible !== false && (
          <g>
            {slices.map((slice, index) => {
              const legendX = 420;
              const legendY = 40 + index * 25;
              return (
                <g key={`legend-${index}`}>
                  <rect x={legendX} y={legendY} width={16} height={16} fill={slice.color} />
                  <text x={legendX + 20} y={legendY + 13} fontSize="12" fill="#374151">
                    {slice.name}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    );
  }, [slices, control.config, pieConfig]);

  return (
    <BaseChartComponent control={control} value={value} chartType="pie-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderPie}</div>
    </BaseChartComponent>
  );
});

PieChartControl.displayName = 'PieChartControl';

export default PieChartControl;
